import { DialogDesignTokens } from '@primeuix/themes/types/dialog';

import { createModalTokens } from '../tokens/components.js';
import { SEMANTIC, type SemanticTokens } from '../tokens/semantic.js';

/**
 * Dialog Component Configuration
 */
export function createDialog(s: SemanticTokens): DialogDesignTokens {
  const modalTokens = createModalTokens(s);
  return {
    root: {
      background: modalTokens.dialogBg,
      borderColor: modalTokens.dialogBorder,
      color: modalTokens.fg,
      borderRadius: modalTokens.base.radius,
      shadow: modalTokens.shadow,
    },
    header: {
      padding: modalTokens.header.padding,
      gap: modalTokens.header.gap,
    },
    content: {
      padding: `0 ${modalTokens.base.paddingX} ${modalTokens.base.paddingY} ${modalTokens.base.paddingX}`,
    },
    footer: {
      padding: `0 ${modalTokens.base.paddingX} ${modalTokens.base.paddingY} ${modalTokens.base.paddingX}`,
    },
    title: {
      fontSize: modalTokens.title.fontSize,
      fontWeight: modalTokens.title.fontWeight,
    },
    colorScheme: {
      light: {
        root: {
          background: modalTokens.dialogBg,
          borderColor: modalTokens.dialogBorder,
          color: modalTokens.fg,
          shadow: modalTokens.shadow,
          borderRadius: modalTokens.base.radius,
        },
      },
      dark: {
        root: {
          background: modalTokens.dialogBg,
          borderColor: modalTokens.dialogBorder,
          color: modalTokens.fg,
          shadow: modalTokens.shadow,
          borderRadius: modalTokens.base.radius,
        },
      },
    },
    css: () => `
    .p-dialog .p-dialog-header {
      gap: ${modalTokens.header.gap};
    }

    .p-dialog .p-dialog-header .p-dialog-header-actions {
      display: flex;
      align-items: center;
      gap: ${modalTokens.footer.gap};
    }
    
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button {
      height: ${modalTokens.closeBtn.size};
      width: ${modalTokens.closeBtn.size};
      min-width: ${modalTokens.closeBtn.size};
      min-height: ${modalTokens.closeBtn.size};
      padding: ${modalTokens.closeBtn.padding};
      border-radius: ${modalTokens.closeBtn.radius};
      border: none;
      background: ${modalTokens.closeBtn.bg};
      color: ${modalTokens.closeBtn.fg};
      font-weight: ${modalTokens.closeBtn.fontWeight};
      font-size: ${modalTokens.closeBtn.fontSize};
      transition: background-color ${modalTokens.closeBtn.transition}, color ${modalTokens.closeBtn.transition};
    }
    
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button .p-icon { 
      width: ${modalTokens.closeBtn.iconSize}; 
      height: ${modalTokens.closeBtn.iconSize};
      margin: 0;
      color: ${modalTokens.closeBtn.fg};
    }
    
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button:not(:disabled):hover { 
      background: ${modalTokens.closeBtn.bgHover}; 
      color: ${modalTokens.closeBtn.fg};
      border: none;
    }
    
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button:not(:disabled):hover .p-icon {
      color: ${modalTokens.closeBtn.fg};
    }
    
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button:not(:disabled):active {
      background: ${modalTokens.closeBtn.bgActive};
      color: ${modalTokens.closeBtn.fg};
      border: none;
    }
    
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button:not(:disabled):active .p-icon {
      color: ${modalTokens.closeBtn.fg};
    }
    
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button:disabled,
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button.p-disabled {
      background: ${modalTokens.closeBtn.bgDisabled};
      color: ${modalTokens.closeBtn.fgDisabled};
      border: none;
    }
    
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button:disabled .p-icon,
    .p-dialog .p-dialog-header .p-dialog-header-actions .p-button.p-disabled .p-icon {
      color: ${modalTokens.closeBtn.fgDisabled};
    }
    
    .p-dialog .p-dialog-content .p-dialog-message {
      font-size: ${modalTokens.content.fontSize};
      color: ${modalTokens.fg};
      line-height: ${modalTokens.content.lineHeight};
    }
    
    /* When content is empty, remove bottom padding from header */
    .p-dialog:has(.p-dialog-content:not(:has(*))) .p-dialog-header {
      padding-bottom: 0;
    }

    .p-dialog .p-dialog-footer {
      display: flex;
      gap: ${modalTokens.footer.gap};
      justify-content: space-between;
      border-top: none;
    }

    /* Prevent dialog from ever overflowing the viewport */
    .p-dialog {
      max-width: calc(100vw - 2rem);
    }

    /* Title wraps to multiple lines instead of squeezing the close button */
    .p-dialog .p-dialog-header {
      align-items: flex-start;
    }

    .p-dialog .p-dialog-title {
      flex: 1;
      min-width: 0;
      word-break: break-word;
      overflow-wrap: break-word;
    }

    /* Mobile: fill the full viewport */
    @media (max-width: 350px) {
      .p-dialog {
        width: 100dvw;
        height: 100dvh;
        max-width: 100dvw;
        max-height: 100dvh;
        margin: 0.1rem;
      }
        
      .p-dialog .p-dialog-content {
        height: 100%;
        overflow-y: auto;
      }
    }
  `,
  };
}
export const dialog = createDialog(SEMANTIC);
